import { useQueryClient } from '@tanstack/react-query';
import Link from 'next/link';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTrackAutomationRunsLoadMore } from '@air/analytics';
import { ChevronLeftIcon, Repeat2Icon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { Spinner } from '@air/primitive-spinner';

import { usePrivateAutomationRunSessionOpenAnalytics } from '~/components/Automations/hooks/usePrivateAutomationRunSessionOpenAnalytics';
import { browserTimeZone } from '~/components/Automations/utils/automationSchedule';
import { PrivateWorkspaceAgentPanelAutomationRunRow } from '~/components/v2/components/workspace/workspace-agent-panel/components/tasks/PrivateWorkspaceAgentPanelAutomationRunRow';
import { WorkspaceAgentPanelMyTasksFooter } from '~/components/v2/components/workspace/workspace-agent-panel/components/tasks/WorkspaceAgentPanelMyTasksFooter';
import { usePrivateWorkspaceAgentPanelSession } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPanelProvider';
import {
  resolveWorkspaceAgentThreadReopenedStatus,
  toWorkspaceAgentThreadReopenedStatus,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/resolveWorkspaceAgentThreadReopenedStatus';
import {
  formatWorkspaceAgentAutomationDisplayName,
  formatWorkspaceAgentAutomationRunsSubtitle,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentPanelAutomationDisplay';
import { Pathnames } from '~/components/v2/constants/pathnames';
import { useWorkspaceAgentPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceAgentPanelStore';
import { usePrivateAutomation } from '~/hooks/queries/automations/usePrivateAutomation';
import {
  type AutomationRun,
  usePrivateAutomationRunsList,
} from '~/hooks/queries/automations/usePrivateAutomationRunsList';

export type PrivateWorkspaceAgentPanelAutomationRunsViewProps = {
  automationId: string;
  isStartNewTaskDisabled: boolean;
  onBack: () => void;
  onStartNewTask: () => void;
  workspaceId: string;
};

export const PrivateWorkspaceAgentPanelAutomationRunsView = memo(
  ({
    automationId,
    isStartNewTaskDisabled,
    onBack,
    onStartNewTask,
    workspaceId,
  }: PrivateWorkspaceAgentPanelAutomationRunsViewProps) => {
    const backButtonRef = useRef<HTMLButtonElement>(null);
    const [viewerTimeZone] = useState(browserTimeZone);
    const [now] = useState(() => new Date());
    const queryClient = useQueryClient();
    const panelSession = usePrivateWorkspaceAgentPanelSession();
    const setPanelView = useWorkspaceAgentPanelStore((state) => state.setPanelView);
    const { trackAutomationRunSessionOpen } = usePrivateAutomationRunSessionOpenAnalytics();
    const { trackAutomationRunsLoadMore } = useTrackAutomationRunsLoadMore();

    const {
      data: automation,
      isError: isAutomationError,
      isLoading: isAutomationLoading,
    } = usePrivateAutomation({ automationId, workspaceId }, { enabled: true });

    const {
      data: runsData,
      fetchNextPage,
      hasNextPage,
      isError: isRunsError,
      isFetchingNextPage,
      isLoading: isRunsLoading,
    } = usePrivateAutomationRunsList({
      automationId,
      limit: 25,
      workspaceId,
    });

    const runs = useMemo(() => runsData?.pages.flatMap((page) => page.data) ?? [], [runsData]);

    const title = automation ? formatWorkspaceAgentAutomationDisplayName(automation) : 'Automation';
    const subtitle =
      automation && !isRunsLoading
        ? formatWorkspaceAgentAutomationRunsSubtitle({
            cronSchedule: automation.cronSchedule,
            now,
            runCount: runs.length,
            timeZone: viewerTimeZone,
          })
        : null;

    const handleOpenRun = useCallback(
      (sessionId: string, run: AutomationRun) => {
        panelSession.selectSession(sessionId);
        setPanelView('chat');

        const fallbackThreadReopenedStatus =
          toWorkspaceAgentThreadReopenedStatus(panelSession.sessions.find((entry) => entry.id === sessionId)?.status) ??
          'active';

        void resolveWorkspaceAgentThreadReopenedStatus({
          queryClient,
          sessions: panelSession.sessions,
          sessionId,
          workspaceId,
        })
          .then((threadReopenedStatus) => {
            trackAutomationRunSessionOpen({
              automationId,
              location: 'agent_panel',
              run,
              sessionId,
              threadReopenedStatus,
            });
          })
          .catch(() => {
            trackAutomationRunSessionOpen({
              automationId,
              location: 'agent_panel',
              run,
              sessionId,
              threadReopenedStatus: fallbackThreadReopenedStatus,
            });
          });
      },
      [automationId, panelSession, queryClient, setPanelView, trackAutomationRunSessionOpen, workspaceId],
    );

    const handleLoadMore = useCallback(() => {
      trackAutomationRunsLoadMore({ automation_id: automationId, location: 'agent_panel' });
      void fetchNextPage();
    }, [automationId, fetchNextPage, trackAutomationRunsLoadMore]);

    useEffect(() => {
      backButtonRef.current?.focus();
    }, []);

    const showLoading = isAutomationLoading || isRunsLoading;
    const showError = (isAutomationError || isRunsError) && !showLoading;

    return (
      <div className="flex min-h-0 flex-1 flex-col" data-testid="WORKSPACE_AGENT_PANEL_AUTOMATION_RUNS_VIEW">
        <div className="flex min-h-0 flex-1 flex-col">
          <div
            className="scrollbar scrollbar-track-gray-2 scrollbar-thumb-gray-7 [&:not(:hover)]:scrollbar-track-transparent min-h-0 flex-1 overflow-y-auto px-3 py-3"
            data-testid="WORKSPACE_AGENT_PANEL_AUTOMATION_RUNS_SCROLL"
          >
            <button
              aria-label="Back to My tasks"
              className="mb-3 -ml-1.5 flex min-w-0 items-center gap-0.5 rounded-4 px-1 py-0.5 font-medium ads:text-secondary text-12 text-gray-11 transition-colors ads:hover:bg-hover-subtle hover:bg-gray-2 ads:hover:text-primary hover:text-gray-12 focus-visible:outline-none ads:focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-altitude-8"
              data-testid="WORKSPACE_AGENT_PANEL_AUTOMATION_RUNS_BACK"
              onClick={onBack}
              ref={backButtonRef}
              type="button"
            >
              <ChevronLeftIcon aria-hidden className="size-4 shrink-0" />
              <span className="min-w-0 truncate">Back</span>
            </button>

            {showLoading ? (
              <div className="flex justify-center py-8">
                <Spinner
                  aria-label="Loading automation runs"
                  className="size-6 ads:text-icon-subtle text-gray-9"
                  role="status"
                />
              </div>
            ) : null}

            {showError ? (
              <p className="ads:text-error text-14 text-gray-10" role="alert">
                {"Couldn't load this automation."}
              </p>
            ) : null}

            {!showLoading && !showError && automation ? (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2 ads:border-default border-gray-4 border-b pb-4">
                  <div className="flex items-start gap-2">
                    <span className="flex size-8 shrink-0 items-center justify-center rounded-6 border ads:border-default border-gray-4 ads:bg-raised bg-gray-2 ads:text-icon-default text-gray-10">
                      <Repeat2Icon aria-hidden size="sm" />
                    </span>
                    <div className="flex min-w-0 flex-1 flex-col gap-2">
                      <div>
                        <h2 className="font-medium ads:text-primary text-16 text-gray-12 leading-5">{title}</h2>
                        {subtitle ? (
                          <p className="mt-1 ads:text-metadata text-12 text-gray-8 leading-4">{subtitle}</p>
                        ) : null}
                      </div>
                      <Button
                        appearance="outline"
                        asChild
                        className="w-fit"
                        color="gray"
                        data-testid="WORKSPACE_AGENT_PANEL_AUTOMATION_VIEW_AUTOMATION"
                        size="small"
                      >
                        <Link
                          href={{
                            pathname: Pathnames.AutomationsByAutomationId,
                            query: { automationId: automation.id },
                          }}
                        >
                          View automation
                        </Link>
                      </Button>
                    </div>
                  </div>
                </div>

                <ul className="m-0 flex list-none flex-col gap-1 p-0">
                  {runs.map((run) => (
                    <PrivateWorkspaceAgentPanelAutomationRunRow
                      key={run.id}
                      automationCreatedBy={automation?.createdBy}
                      now={now}
                      onOpenRun={handleOpenRun}
                      run={run}
                      timeZone={viewerTimeZone}
                    />
                  ))}
                </ul>

                {hasNextPage ? (
                  <Button
                    appearance="outline"
                    color="gray"
                    data-testid="WORKSPACE_AGENT_PANEL_AUTOMATION_RUNS_LOAD_MORE"
                    isLoading={isFetchingNextPage}
                    onClick={handleLoadMore}
                    size="medium"
                  >
                    Load more
                  </Button>
                ) : null}

                {!isRunsLoading && runs.length === 0 ? (
                  <p className="ads:text-metadata text-14 text-gray-10">No runs yet.</p>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        <WorkspaceAgentPanelMyTasksFooter isDisabled={isStartNewTaskDisabled} onStartNewTask={onStartNewTask} />
      </div>
    );
  },
);

PrivateWorkspaceAgentPanelAutomationRunsView.displayName = 'PrivateWorkspaceAgentPanelAutomationRunsView';
